import { useAuth } from '@hb/react'
import {
  AssessmentDocument,
  ElectronicConsent,
  electronicConsentCheckboxes,
  electronicConsentHeader,
  electronicConsentPreface,
  isSignableDocument,
  SignableDocument,
  UserElectronicConsent,
  WithId,
} from '@hb/shared'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

// Agreement-type-specific config, injected by the outer provider (patient vs provider).
// Lets the shared modal/overlay serve both the patient electronic consent and the provider
// practice-contract agreement without forking the component tree.
export interface AgreementConfig {
  // header shown at the top of the agreement form
  title: string
  preface: string
  checkboxes: string[]
  // label used in the preview row / view button (e.g. 'Electronic Consent Agreement')
  previewTitle: string
  // fallback word in the "Signed by ..." tooltip when the signer user isn't loaded
  signerFallbackLabel: string
  // performs the confirm callable for this agreement type
  confirm: () => Promise<unknown>
}

export const defaultPatientAgreementConfig: AgreementConfig = {
  title: electronicConsentHeader,
  preface: electronicConsentPreface,
  checkboxes: electronicConsentCheckboxes,
  previewTitle: 'Electronic Consent Agreement',
  signerFallbackLabel: 'patient',
  confirm: async () => {},
}

interface ElectronicConsentContextValue {
  signerId: string | null
  toBeSignedByMe: boolean
  consent: WithId<UserElectronicConsent> | null
  sortedSubmissions: ElectronicConsent[] | null
  mostRecentSubmission: ElectronicConsent | null
  consentLoading: boolean
  consentLoadingError: string | null
  config: AgreementConfig
}

interface ElectronicConsentOverlayContextValue {
  isOpen: boolean
  consentRequired: boolean
  onClose: (forceClose?: boolean) => void
  onOpen: () => void
  // the document being gated (consent form or practice contract); null when not signable
  consentForm: WithId<SignableDocument> | null
}

const ElectronicConsentOverlayContext = createContext<ElectronicConsentOverlayContextValue>({
  isOpen: false,
  consentRequired: false,
  onClose: () => {},
  onOpen: () => {},
  consentForm: null,
})

export const ElectronicConsentContext = createContext<ElectronicConsentContextValue>({
  signerId: null,
  toBeSignedByMe: false,
  consent: null,
  sortedSubmissions: null,
  mostRecentSubmission: null,
  consentLoading: false,
  consentLoadingError: null,
  config: defaultPatientAgreementConfig,
})

const ElectronicConsentOverlayProviderBody = ({
  document,
  onCloseDocument,
  children,
}: PropsWithChildren<{
  document: WithId<SignableDocument> | WithId<AssessmentDocument>
  onCloseDocument: () => void
}>) => {
  const { consent, consentLoading } = useContext(ElectronicConsentContext)
  const { authUser } = useAuth()

  const consentForm = useMemo(() => {
    if (isSignableDocument(document)) {
      return document
    }
    return null
  }, [document])
  const toBeSignedByMe = useMemo(() => {
    if (isSignableDocument(document)) {
      return document.toBeSignedByUser === authUser?.uid
    }
    return false
  }, [document, authUser])

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const hasSubmitted = Object.values(consent?.submissions ?? {}).length > 0
    setIsOpen(toBeSignedByMe && !hasSubmitted)
  }, [consent, toBeSignedByMe])

  const mostRecentConsent = useMemo(() => {
    if (!consent) return null
    return Object.values(consent?.submissions ?? {})
      .filter(t => typeof t !== 'string')
      .sort((a, b) => new Date(b.signedOn).getTime() - new Date(a.signedOn).getTime())[0]
  }, [consent])

  const consentRequired = useMemo(() => {
    if (!authUser) return false
    if (isSignableDocument(document)) {
      return !consentLoading && !mostRecentConsent && toBeSignedByMe
    }
    return false
  }, [document, authUser, toBeSignedByMe, mostRecentConsent, consentLoading])
  const onClose = useCallback(
    (forceClose = false) => {
      if (consentRequired && !forceClose) {
        onCloseDocument()
      } else {
        setIsOpen(false)
      }
    },
    [consentRequired, onCloseDocument],
  )

  useEffect(() => {
    if (consentRequired) {
      setIsOpen(true)
    }
  }, [consentRequired])

  const contextValue = useMemo<ElectronicConsentOverlayContextValue>(
    () => ({
      isOpen,
      consentRequired,
      consentForm,
      onClose,
      onOpen: () => setIsOpen(true),
    }),
    [isOpen, consentRequired, consentForm, onClose],
  )

  return (
    <ElectronicConsentOverlayContext.Provider value={contextValue}>
      {children}
    </ElectronicConsentOverlayContext.Provider>
  )
}

export const ElectronicConsentOverlayProvider = ({
  children,
  document,
  onCloseDocument,
}: PropsWithChildren<{
  document: WithId<SignableDocument> | WithId<AssessmentDocument>
  onCloseDocument: () => void
}>) => (
  <ElectronicConsentOverlayProviderBody document={document} onCloseDocument={onCloseDocument}>
    {children}
  </ElectronicConsentOverlayProviderBody>
)

export const useElectronicConsentOverlay = () => useContext(ElectronicConsentOverlayContext)
export const useElectronicConsent = () => useContext(ElectronicConsentContext)
